import {useMemo, type FC} from 'react'
import {BranchFilterProvider} from './providers/branch'
import {EvaluateStatusFilterProvider} from './providers/evaluate-status'
import {RulesetFilterProvider} from './providers/ruleset'
import {UserFilterProvider} from './providers/user'
import {TimePeriodFilterProvider} from './providers/time-period'
import {RuleStatusFilterProvider} from './providers/rule-status'
import type {InsightsFilter, Ruleset, SourceType} from '../../types/rules-types'
import {OrgFilterProvider} from '@github-ui/filter/providers'
import {ShaFilterProvider} from './providers/sha'
import {SecurityProductsRepositoryFilterProvider} from './providers/security-products-repository-filter-provider'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {
  SecurityProductsFilterBar,
  extractAsUser,
  getLogin,
  getName,
  timePeriodField,
  repositoryField,
  organizationField,
  timePeriodQueryField,
  orgQueryField,
  repositoryQueryField,
  singleValueFilterTypes,
  type FilterFieldConfig,
  type QueryFieldConfig,
} from './SecurityProductsFilterBar'
import type {FilterProvider} from '@github-ui/filter'
import type {Organization, Enterprise} from '@github-ui/repos-types'
import type {Repository} from '@github-ui/current-repository'

const insightsFieldConfigs: Array<FilterFieldConfig<InsightsFilter>> = [
  {key: 'actor', queryKey: 'user', getValue: getLogin},
  timePeriodField,
  {key: 'ruleset', queryKey: 'ruleset', getValue: getName, quoted: true},
  {key: 'branch', queryKey: 'branch'},
  {key: 'ruleStatus', queryKey: 'rule-status'},
  {key: 'evaluateStatus', queryKey: 'evaluate-status'},
  repositoryField,
  organizationField,
]

const createInsightsQueryFieldConfigs = (
  rulesets: Ruleset[],
  reposRuleSuitesListBySha: boolean,
  sourceType: SourceType,
): Array<QueryFieldConfig<InsightsFilter>> => [
  {providerKey: 'user', filterKey: 'actor', getValue: extractAsUser},
  timePeriodQueryField,
  {
    providerKey: 'ruleset',
    filterKey: 'ruleset',
    getValue: (values: Array<{displayName?: string}>) => rulesets.find(r => r.name === values[0]?.displayName),
  },
  {providerKey: 'rule-status', filterKey: 'ruleStatus'},
  {providerKey: 'evaluate-status', filterKey: 'evaluateStatus'},
  repositoryQueryField,
  orgQueryField,
  ...(reposRuleSuitesListBySha ? [{providerKey: 'sha', filterKey: 'afterOid' as const}] : []),
  ...(sourceType === 'repository' ? [{providerKey: 'branch', filterKey: 'branch' as const}] : []),
]

type InsightsFilterBar2Props = {
  filter: InsightsFilter
  source: Repository | Organization | Enterprise
  sourceType: SourceType
  businessSlug?: string
  rulesets: Ruleset[]
}

export const InsightsFilterBar2: FC<InsightsFilterBar2Props> = ({
  filter,
  source,
  sourceType,
  businessSlug,
  rulesets,
}) => {
  const reposRuleSuitesListBySha = useFeatureFlag('repos_rule_suites_list_by_sha')

  const filterProviders = useMemo(() => {
    const providers: FilterProvider[] = [
      new EvaluateStatusFilterProvider(),
      new RulesetFilterProvider(rulesets),
      new UserFilterProvider(),
      new TimePeriodFilterProvider(),
      new RuleStatusFilterProvider(),
    ]

    if (sourceType === 'repository') {
      const repo = source as Repository
      providers.unshift(new BranchFilterProvider(repo.ownerLogin, repo.name))
      if (reposRuleSuitesListBySha) {
        providers.unshift(new ShaFilterProvider())
      }
    }

    if (sourceType === 'organization') {
      providers.unshift(new SecurityProductsRepositoryFilterProvider(source.name))
    }

    if (sourceType === 'enterprise' && businessSlug) {
      providers.unshift(new OrgFilterProvider({businessSlug, filterTypes: singleValueFilterTypes}))
    }

    return providers
  }, [rulesets, sourceType, reposRuleSuitesListBySha, businessSlug, source])

  const queryFieldConfigs = useMemo(
    () => createInsightsQueryFieldConfigs(rulesets, reposRuleSuitesListBySha, sourceType),
    [rulesets, reposRuleSuitesListBySha, sourceType],
  )

  return (
    <SecurityProductsFilterBar<InsightsFilter>
      id="rules-insights-filter"
      label="Filter rules insights"
      filter={filter}
      fieldConfigs={insightsFieldConfigs}
      queryFieldConfigs={queryFieldConfigs}
      filterProviders={filterProviders}
      getIndexPath={insightsIndexPath}
    />
  )
}
